import { useMemo } from 'react';
import { IntlShape, useIntl } from 'react-intl';
import { z } from 'zod';
import { QueueAction } from '@rossum/api-client/schemas';
import { IconEye, IconEyeOff, IconEyeQuestion } from '@rossum/ui/icons/tabler';
import { Autocomplete, TextField } from '@rossum/ui/material';
import { getUnsafe } from '../../../lib/helpers';
import { getSchemaFields } from '../../formulas/FormulaEditor/helpers';
import { createForm } from './createForm';
import { createHeader } from './createHeader';

const MAX_ENTRIES = 3;

const visibilityActionPayloadSchema = (intl?: IntlShape) =>
  z.object({
    schema_id: z.string().optional(),
    schema_ids: z
      .array(z.string())
      .optional()
      .refine(
        val => val && val.length > 0,
        intl?.formatMessage({
          id: 'features.queueSettings.rules.actions.visibility.payload.schemaId.required',
        })
      ),
  });

const getSelectedSchemaFields = (
  value: z.infer<ReturnType<typeof visibilityActionPayloadSchema>>,
  schemaFieldOptions: ReturnType<typeof getSchemaFields>
) => {
  const schemaIds = value.schema_ids;
  if (schemaIds?.length) {
    return schemaFieldOptions.filter(label => schemaIds.includes(label.id));
  }

  if (value.schema_id) {
    const chosenSchema = schemaFieldOptions.find(f => f.id === value.schema_id);
    return chosenSchema ? [chosenSchema] : [];
  }

  return [];
};

const VisibilityActionForm = createForm(
  ({ value, onChange, schemaContent, errors }) => {
    const intl = useIntl();
    const schemaFieldOptions = useMemo(
      () =>
        getSchemaFields(schemaContent).filter(
          ({ isInsideMultivalue }) => !isInsideMultivalue
        ),
      [schemaContent]
    );
    const schemaFields = useMemo(() => {
      return getSelectedSchemaFields(value, schemaFieldOptions);
    }, [schemaFieldOptions, value]);

    return (
      <Autocomplete
        multiple
        size="small"
        options={schemaFieldOptions}
        getOptionLabel={option => `${option.label} (${option.id})`}
        value={schemaFields}
        onChange={(_e, vs) => {
          onChange({
            ...value,
            schema_ids: vs.map(v => v.id),
            schema_id: undefined,
          });
        }}
        renderInput={params => (
          <TextField
            {...params}
            label={intl.formatMessage({
              id: 'features.queueSettings.rules.actions.visibility.form.fields',
            })}
            placeholder={intl.formatMessage({
              id: 'features.queueSettings.rules.actions.visibility.form.chooseFields',
            })}
            InputLabelProps={{ shrink: true }}
            error={getUnsafe(errors, 'schema_ids') !== undefined}
            helperText={getUnsafe(errors, 'schema_ids.message')?.toString()}
          />
        )}
      />
    );
  },
  (intl?: IntlShape) => visibilityActionPayloadSchema(intl)
);

const VisibilityActionHeader = createHeader(
  ({ value, schemaContent, RenderComponent }) => {
    const intl = useIntl();
    const schemaFieldOptions = useMemo(
      () =>
        getSchemaFields(schemaContent).filter(
          ({ isInsideMultivalue }) => !isInsideMultivalue
        ),
      [schemaContent]
    );

    const schemaFields = useMemo(() => {
      return getSelectedSchemaFields(value, schemaFieldOptions);
    }, [schemaFieldOptions, value]);

    return (
      <RenderComponent
        entries={[
          ...schemaFields.slice(0, MAX_ENTRIES).flatMap(schemaField => [
            {
              key: `schema_field_${schemaField.id}`,
              label: intl.formatMessage({
                id: 'features.rules.testSection.displayKey.schemaFields',
              }),
              value: schemaField.label,
            },
          ]),
          ...(schemaFields.length > MAX_ENTRIES
            ? [
                {
                  key: 'more_schema_fields',
                  value: `+${schemaFields.length - MAX_ENTRIES}`,
                },
              ]
            : []),
        ]}
      />
    );
  },
  (intl?: IntlShape) => visibilityActionPayloadSchema(intl)
);

const showFieldAction = {
  icon: IconEye,
  intlKey: 'showField' as const,
  resolveAction: (a: QueueAction) => a.type === 'show_field',
  serialized: { type: 'show_field' as const },
  groupIntlKey: 'visibility' as const,
  form: VisibilityActionForm,
  header: VisibilityActionHeader,
  supportedEvents: ['validation'] as const,
  payloadSchema: (intl?: IntlShape) => visibilityActionPayloadSchema(intl),
};

const hideFieldAction = {
  icon: IconEyeOff,
  intlKey: 'hideField' as const,
  resolveAction: (a: QueueAction) => a.type === 'hide_field',
  serialized: { type: 'hide_field' as const },
  groupIntlKey: 'visibility' as const,
  form: VisibilityActionForm,
  header: VisibilityActionHeader,
  supportedEvents: ['validation'] as const,
  payloadSchema: (intl?: IntlShape) => visibilityActionPayloadSchema(intl),
};

const showHideFieldAction = {
  icon: IconEyeQuestion,
  intlKey: 'showHideField' as const,
  resolveAction: (a: QueueAction) => a.type === 'show_hide_field',
  serialized: { type: 'show_hide_field' as const },
  groupIntlKey: 'visibility' as const,
  form: VisibilityActionForm,
  header: VisibilityActionHeader,
  supportedEvents: ['validation'] as const,
  payloadSchema: (intl?: IntlShape) => visibilityActionPayloadSchema(intl),
};

export const visibilityActions = [
  showHideFieldAction,
  showFieldAction,
  hideFieldAction,
];
